import { FC, ReactNode } from 'react'
import { useTranslation } from 'react-i18next'

import CheckCircleRoundedIcon from '@mui/icons-material/CheckCircleRounded'
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined'
import { alpha, Box, Paper, Theme, Tooltip, Typography, useTheme } from '@mui/material'

import { borderRadiusDefault } from '../../../../themes/theme'
import { GroupedTagComparison, TagStats } from '../../helpers/creativeTagPerformance'
import { clickableProps } from '../../helpers/interactive'
import { formatPerf } from '../../helpers/scoreDisplay'
import { focusRingSx } from '../../helpers/styles'
import { useUAInsightsActions } from '../../hooks/useUAInsightsNavigation'
import { useUATaxonomy } from '../../hooks/useUATaxonomy'

/** Three buckets a tag can land in once both games are scored against the carrier floor. */
export type TagBucket = 'shared' | 'sourceOnly' | 'targetOnly'

/**
 * Stable DOM id for a tag's diagnosis card, so cross-tab affordances (e.g. the
 * Overview's Creative Tags preview) can scroll the full tab straight to a tag.
 * Non-id-safe characters are collapsed to underscores; both the writer (the
 * card) and reader (the scroll effect) go through this helper so the ids
 * always match.
 */
export const creativeTagAnchorId = (tagId: string): string => `ua-ct-card-${tagId.replace(/[^a-zA-Z0-9_-]/g, '_')}`

interface DiagnosisColumnProps {
  bucket: TagBucket
  /** Column header accent + card left-border colour. */
  accent: string
  title: ReactNode
  rows: GroupedTagComparison[]
  /**
   * Max number of cards to render. Pass `Infinity` to render all rows. The
   * header chip shows the *full* count regardless so the user always sees
   * how many tags are hidden behind the limit.
   */
  visibleLimit: number
  /** Empty-state copy rendered when `rows` is empty. */
  emptyText: string
  sourceName: string
  targetName: string
  sourceColor: string
  targetColor: string
}

/**
 * One column of the three-column tag-bucket layout. Renders the bucket
 * header (with count chip) + a vertical stack of {@link DiagnosisCard}s,
 * truncated to `visibleLimit` cards. Sort order is the caller's job — this
 * component is purely presentational.
 */
export const DiagnosisColumn: FC<DiagnosisColumnProps> = ({
  bucket,
  accent,
  title,
  rows,
  visibleLimit,
  emptyText,
  sourceName,
  targetName,
  sourceColor,
  targetColor,
}) => {
  const visibleRows = Number.isFinite(visibleLimit) ? rows.slice(0, visibleLimit) : rows
  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.25, minWidth: 0 }}>
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 0.75,
          px: 1.25,
          py: 0.5,
          borderRadius: borderRadiusDefault,
          backgroundColor: alpha(accent, 0.12),
          color: accent,
        }}
      >
        {bucket === 'shared' ? (
          <CheckCircleRoundedIcon sx={{ fontSize: 16 }} />
        ) : (
          <Box aria-hidden sx={{ width: 11, height: 11, borderRadius: '50%', backgroundColor: accent, flexShrink: 0 }} />
        )}
        <Typography
          variant="caption"
          sx={{
            fontWeight: 700,
            letterSpacing: '0.04em',
            textTransform: 'uppercase',
            color: 'inherit',
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
          }}
        >
          {title}
        </Typography>
        <Typography variant="caption" sx={{ fontWeight: 700, color: 'inherit', opacity: 0.7, ml: 'auto', pl: 0.5 }}>
          {rows.length}
        </Typography>
      </Box>
      {rows.length === 0 ? (
        <Paper
          variant="outlined"
          sx={{
            p: 1.5,
            borderStyle: 'dashed',
            borderColor: 'divider',
            backgroundColor: 'transparent',
            borderRadius: borderRadiusDefault,
          }}
        >
          <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic' }}>
            {emptyText}
          </Typography>
        </Paper>
      ) : (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
          {visibleRows.map((row) => (
            <DiagnosisCard
              key={row.tag.id}
              row={row}
              accent={accent}
              bucket={bucket}
              sourceName={sourceName}
              targetName={targetName}
              sourceColor={sourceColor}
              targetColor={targetColor}
            />
          ))}
        </Box>
      )}
    </Box>
  )
}

interface DiagnosisCardProps {
  row: GroupedTagComparison
  accent: string
  bucket: TagBucket
  sourceName: string
  targetName: string
  sourceColor: string
  targetColor: string
}

/**
 * A single tag card inside a {@link DiagnosisColumn}. Layout:
 *
 *   [tag name (clickable → tag details)]
 *   [dimension caption]
 *   [per-game readout row(s)]
 *
 * Per-game readout shows the primary `perf %` pill followed by a muted
 * caption with the carrier count + share-of-ads %, so the user can sanity-
 * check the perf number without it dominating the visual hierarchy.
 *
 * `shared` cards render two readout rows (source then target); one-sided
 * cards render the single relevant row only.
 */
export const DiagnosisCard: FC<DiagnosisCardProps> = ({ row, accent, bucket, sourceName, targetName, sourceColor, targetColor }) => {
  const { openTag } = useUAInsightsActions()
  const { tagLabel, dimensionLabel } = useUATaxonomy()
  const { t } = useTranslation('ua-insights')

  return (
    <Paper
      elevation={0}
      id={creativeTagAnchorId(row.tag.id)}
      aria-label={t('open_tag_details', { name: tagLabel(row.tag) })}
      {...clickableProps(() => openTag(row.tag.id))}
      sx={{
        p: 1.25,
        display: 'flex',
        flexDirection: 'column',
        gap: 0.75,
        boxSizing: 'border-box',
        scrollMarginTop: 88,
        cursor: 'pointer',
        backgroundColor: 'background.paper',
        borderRadius: borderRadiusDefault,
        border: `1px solid ${alpha(accent, 0.7)}`,
        transition: (theme) => theme.transitions.create(['border-color', 'box-shadow', 'background-color']),
        '&:hover': {
          borderColor: accent,
          boxShadow: (theme) => `0 2px 8px ${alpha(accent, 0.25)}`,
          backgroundColor: alpha(accent, 0.04),
        },
        ...focusRingSx(),
      }}
    >
      <Box sx={{ display: 'flex', flexDirection: 'column', minWidth: 0 }}>
        <Typography variant="subtitle2" sx={{ color: accent, fontWeight: 'bold', lineHeight: 1.3 }}>
          {tagLabel(row.tag)}
        </Typography>
        {row.dimension && (
          <Typography variant="caption" color="text.secondary" sx={{ lineHeight: 1.3 }}>
            {dimensionLabel(row.dimension)}
          </Typography>
        )}
      </Box>
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
        {bucket !== 'targetOnly' && row.src && <PerGameReadout label={sourceName} stats={row.src} color={sourceColor} />}
        {bucket !== 'sourceOnly' && row.tgt && <PerGameReadout label={targetName} stats={row.tgt} color={targetColor} />}
      </Box>
    </Paper>
  )
}

interface PerGameReadoutProps {
  label: string
  stats: TagStats
  color: string
}

/**
 * Single per-game readout line: a coloured `[GAME XX%]` pill followed by
 * a muted "N ads · Y% of game" caption. The pill shows the tag's mean Ad
 * Performance — the strongest individual ad in the comparison reads as
 * `100`, every other ad/tag scales against it via the same formula
 * `CreativeAdsTable` uses for its `col_ad_performance` column. The
 * caption surfaces the carrier count (confidence) and share-of-ads
 * (lean) without competing visually.
 */
const PerGameReadout: FC<PerGameReadoutProps> = ({ label, stats, color }) => {
  const theme = useTheme()
  const { t } = useTranslation('ua-insights')
  const tooltip = t('compare_tag_readout_tooltip', {
    perf: formatPerf(stats.perf),
    carriers: stats.carrierCount,
    share: stats.share.toFixed(0),
  })
  return (
    <Tooltip title={tooltip} arrow placement="top">
      <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.75, flexWrap: 'wrap' }}>
        <PerfPill label={label} perf={stats.perf} color={color} theme={theme} />
        <Typography
          variant="caption"
          sx={{
            color: 'text.secondary',
            fontVariantNumeric: 'tabular-nums',
            lineHeight: 1.2,
          }}
        >
          {t('compare_tag_readout_caption', { carriers: stats.carrierCount, share: stats.share.toFixed(0) })}
        </Typography>
      </Box>
    </Tooltip>
  )
}

interface PerfPillProps {
  label: string
  /** Mean Ad Performance for the tag's carrier ads (0-100, leader = 100). */
  perf: number
  color: string
  theme: Theme
}

/**
 * `[GAME XX%]` Ad Performance chip. The number reads on the same 0-100
 * scale as the standalone `CreativeAdsTable.col_ad_performance` — the
 * strongest individual ad in the comparison anchors the scale at 100, and
 * a tag's chip shows the mean Ad Performance across its carrier ads.
 * Coloured per-game (source = radar `secondary.main`, target =
 * `primary.main`) so the pill itself acts as a legend.
 */
const PerfPill: FC<PerfPillProps> = ({ label, perf, color, theme }) => (
  <Box
    sx={{
      display: 'inline-flex',
      alignItems: 'baseline',
      gap: 0.5,
      px: 0.875,
      py: 0.25,
      borderRadius: 999,
      backgroundColor: alpha(color, 0.12),
      borderColor: alpha(color, 0.32),
      borderStyle: 'solid',
      borderWidth: 1,
      maxWidth: '100%',
      minWidth: 0,
    }}
  >
    <Typography
      variant="caption"
      sx={{
        fontSize: '0.62rem',
        fontWeight: 700,
        letterSpacing: '0.05em',
        textTransform: 'uppercase',
        color,
        lineHeight: 1.2,
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        whiteSpace: 'nowrap',
        minWidth: 0,
      }}
    >
      {label}
    </Typography>
    <Typography
      sx={{
        fontSize: '0.78rem',
        fontWeight: 700,
        color: theme.palette.text.primary,
        fontVariantNumeric: 'tabular-nums',
        lineHeight: 1.2,
      }}
    >
      {`${formatPerf(perf)}%`}
    </Typography>
  </Box>
)

interface UnavailableDiagnosisCardProps {
  body: string
}

/**
 * Warning-tinted disclaimer rendered when neither game has any creative-tag
 * data in the current (network/month) scope. Mirrors the styling of the
 * motivation-diagnosis "unavailable" card so the comparison dialog reads
 * with one consistent vocabulary across both tabs.
 */
export const UnavailableDiagnosisCard: FC<UnavailableDiagnosisCardProps> = ({ body }) => {
  const { t } = useTranslation('ua-insights')
  const theme = useTheme()
  const accent = theme.palette.warning.main

  return (
    <Paper
      variant="outlined"
      sx={{
        p: 2.25,
        display: 'flex',
        gap: 1.75,
        alignItems: 'flex-start',
        borderColor: alpha(accent, 0.3),
        backgroundColor: alpha(accent, 0.06),
        borderRadius: borderRadiusDefault,
      }}
    >
      <Box
        sx={{
          width: 36,
          height: 36,
          borderRadius: '50%',
          backgroundColor: alpha(accent, 0.16),
          color: accent,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          flexShrink: 0,
        }}
      >
        <InfoOutlinedIcon fontSize="small" />
      </Box>
      <Box sx={{ minWidth: 0 }}>
        <Typography variant="overline" sx={{ fontWeight: 700, color: accent, letterSpacing: '0.08em', lineHeight: 1.4 }}>
          {t('diagnosis_unavailable_title')}
        </Typography>
        <Typography variant="body2" sx={{ color: 'text.primary', lineHeight: 1.55, mt: 0.25 }}>
          {body}
        </Typography>
      </Box>
    </Paper>
  )
}
